import { Editors } from 'angular-slickgrid';
import { FieldDefinition } from '../field-definition';
import { GenericValidator } from '../../generic-form/model/generic-validator';
import { GenericFormField } from '../../generic-form/model/generic-field';
import { ValidationTypes } from '../../generic-form/enums/validation-types';
import { ControlTypes } from '../../generic-form/enums/control-types';
import { InputTypes } from '../../generic-form/enums/input-type';
import { ValidationConverterService } from '../../services/validation-converter.service';
import { CustomNumberRangeFilter } from '../../generic-table/filters/custom-number-range-filter';
import { INT32_MAX, INT32_MIN } from '../../constants/numeric-constants';
import { BaseFieldBuilder } from './field-builder.base';

export class NumberFieldBuilder extends BaseFieldBuilder {
  private validators: GenericValidator[] = [];
  private _required = false;
  private _isDecimal = false;
  private _decimalPrecision = 2;
  private _decimalScale?: number;
  private _decimalTotalPrecision?: number;

  constructor(key: string, label: string) {
    super(key, label, ControlTypes.Textbox);
    this._type = InputTypes.Number;
  }

  required(): this {
    this._required = true;
    this.validators.push(new GenericValidator({ key: ValidationTypes.Required }));
    return this;
  }

  min(value: number): this {
    this.validators.push(new GenericValidator({ key: ValidationTypes.Min, value }));
    return this;
  }

  max(value: number): this {
    this.validators.push(new GenericValidator({ key: ValidationTypes.Max, value }));
    return this;
  }

  decimal(scale: number = 2, totalPrecision?: number): this {
    this._isDecimal = true;
    const isValidInt = (value: number) => Number.isFinite(value) && Number.isInteger(value);
    const rawPrecision = totalPrecision; 

    const configError =
      !isValidInt(scale) ||
      scale < 0 ||
      (rawPrecision !== undefined && (!isValidInt(rawPrecision) || rawPrecision <= 0)) ||
      (rawPrecision !== undefined && isValidInt(scale) && isValidInt(rawPrecision) && scale > rawPrecision);

    const effectiveScale = isValidInt(scale) && scale >= 0 ? scale : 2;
    const effectivePrecision = rawPrecision !== undefined && isValidInt(rawPrecision) && rawPrecision > 0
      ? rawPrecision
      : undefined;
    const normalizedScale = effectivePrecision !== undefined && effectiveScale > effectivePrecision
      ? effectivePrecision
      : effectiveScale;

    this._decimalPrecision = normalizedScale;
    this._decimalScale = normalizedScale;
    if (effectivePrecision !== undefined) {
      this._decimalTotalPrecision = effectivePrecision;
    }
    this.validators.push(
      new GenericValidator({
        key: ValidationTypes.Decimal,
        value: effectivePrecision !== undefined
          ? { precision: effectivePrecision, scale: normalizedScale, configError, rawScale: scale, rawPrecision }
          : { scale: normalizedScale, configError, rawScale: scale, rawPrecision }
      })
    );

    return this;
  }

  /**
   * Cross-field validation: this field must be less than another field.
   * Useful for min/max ranges, startValue/endValue, etc.
   */
  lessThanField(fieldKey: string): this {
    this.validators.push(
      new GenericValidator({ key: ValidationTypes.LessThanField, value: fieldKey })
    );
    return this;
  }

  /**
   * Cross-field validation: this field must be greater than another field.
   * Useful for min/max ranges, startValue/endValue, etc.
   */
  greaterThanField(fieldKey: string): this {
    this.validators.push(
      new GenericValidator({ key: ValidationTypes.GreaterThanField, value: fieldKey })
    );
    return this;
  }

  build(): FieldDefinition {
    const column = this.getBaseColumnConfig();
    const localizedLabel = this.getLocalizationKey();

    if (!this.readOnlyValue) {
      // Factory to create editor with validation
      column.createEditor = (localizationService: any) => {
        const validationConverter = new ValidationConverterService(localizationService);
        const editorConfig: any = {
          model: this._isDecimal ? Editors.float : Editors.integer,
          required: this._required,
          validator: validationConverter.toSlickGridValidator(
            this.validators,
            this._required,
            this._controlType
          ),
        };

        if (this._isDecimal) {
          const scale = this._decimalScale ?? this._decimalPrecision;
          editorConfig.decimal = scale;
          editorConfig.valueStep = 1 / Math.pow(10, scale);
          editorConfig.maxDecimal = scale;

          if (this._decimalTotalPrecision !== undefined) {
            editorConfig.maxPrecision = this._decimalTotalPrecision;
          }
        }

        if (!this._isDecimal) {
          this.validators.push(
            new GenericValidator({ key: ValidationTypes.Max, value: INT32_MAX }),
            new GenericValidator({ key: ValidationTypes.Min, value: INT32_MIN }),
          );
        }

        return editorConfig;
      };
    }

    // Factory to create number range filter
    column.createFilter = (localizationService: any) => {
      const params: Record<string, any> = { localizationService };
      if (this.filterInputEmptyValueValue) {
        params.inputEmptyValue = this.filterInputEmptyValueValue;
      }
      return {
        model: CustomNumberRangeFilter,
        params,
      };
    };

    return {
      key: this.key,
      label: localizedLabel,
      column,
      fieldOptions: new GenericFormField({
        key: this.key,
        label: localizedLabel,
        controlType: this._controlType,
        type: this._type,
        step: this._isDecimal ? String((1 / Math.pow(10, this._decimalPrecision))) : undefined,
        decimalPrecision: this._decimalTotalPrecision,
        decimalScale: this._decimalScale ?? this._decimalPrecision,
        required: this._required,
        validators: this.validators,
        hidden: this.hideInChildFormsValue,
        hiddenByDefault: this.hideByDefaultValue,
        readOnly: this.readOnlyValue,
        showReadOnlyInForm: this.showReadOnlyInFormValue,
        dependencies: this.dependencyRules.length ? this.dependencyRules : undefined,
        prefilter: this.prefilterOptions,
      }),
    };
  }
}
